import type { GroupResponse } from '@lms/api-client/backend';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@lms/ui/dialog';
import { DropdownMenuItem } from '@lms/ui/dropdown-menu';
import { PenIcon } from 'lucide-react';
import { forwardRef, useState } from 'react';
import { useTranslations } from 'use-intl';
import { DeleteConfirmationMenu } from '@/components/delete-confirmation-menu';
import type { Site } from '@/features/branding/brand-provider';
import BasicDataForm from '@/features/groups/components/basic-group-data';
import { removeGroup } from '@/features/groups/groups-api';

export default function OverflowMenu({
  group,
  site,
  onChanged,
}: {
  group: GroupResponse;
  site: Site;
  onChanged: () => void;
}) {
  const [dialogOpen, setDialogOpen] = useState(false);

  return (
    <DeleteConfirmationMenu
      id={group.id}
      onDelete={removeGroup}
      onDeleted={onChanged}
    >
      <EditGroupDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        site={site}
        group={group}
        onSaved={() => {
          setDialogOpen(false);
          onChanged();
        }}
      />
    </DeleteConfirmationMenu>
  );
}

const EditGroupDialog = forwardRef<
  HTMLDivElement,
  {
    site: Site;
    group: GroupResponse;
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onSaved: () => void;
  }
>((props, forwardedRef) => {
  const { open, onOpenChange, site, group, onSaved, ...itemProps } = props;
  const t = useTranslations('admin');
  const t_g = useTranslations('group_alias');
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <DropdownMenuItem
          {...itemProps}
          ref={forwardedRef}
          onSelect={(event) => {
            event.preventDefault();
          }}
        >
          <PenIcon className="mr-2 h-4 w-4" />
          {t('edit', {
            what: t_g(group.groupType).toLowerCase(),
          })}
        </DropdownMenuItem>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t('edit', {
              what: t_g(group.groupType).toLowerCase(),
            })}
          </DialogTitle>
          <DialogDescription>
            <BasicDataForm site={site} group={group} onSaved={onSaved} />
          </DialogDescription>
        </DialogHeader>
      </DialogContent>
    </Dialog>
  );
});

EditGroupDialog.displayName = 'EditGroupDialog';
